import type { GlyphRenderInstruction } from '@meitu/x-design-text';
import { rgbaUint32ToCss } from '@/utils/color-kit';
import { createSVGElement } from '@/utils/svg-kit';
import { resolveDelegatedFontFamily } from '@/utils/text-kit';
import type { Bounds } from '@/version-types';
import { ITALIC_SKEW_DEG } from '../../constants';
import { getPathBounds } from '../../utils';

/**
 * 正文指令与字符 marker 共同使用的 SVG 节点来源。
 */
export type GlyphNodeSource =
    | GlyphRenderInstruction
    | Readonly<{
          kind: 'marker';
          d: string;
          fill: string;
          transform: string;
      }>;

/**
 * 将 SVG 元素包进一次额外变换。
 *
 * @param node - 已带自身坐标变换的 SVG 元素。
 * @param transform - 需要作用于整个字形的外层变换。
 * @returns 原节点或带变换的 group。
 */
export const wrapTransform = (node: SVGElement, transform?: string): SVGElement => {
    if (!transform) return node;
    const group = createSVGElement('g', { transform });
    group.appendChild(node);
    return group;
};

/**
 * 计算竖排 path 旋转所需的轮廓中心。
 *
 * 正文调用方通常传入本轮已经解析的 bounds；独立节点调用未提供时仍可自行解析，保持公共 helper
 * 的原有行为。两条路径使用同一计算公式。
 *
 * @param path - SVG path 数据。
 * @param cachedBounds - 可选的本轮原始 path bounds。
 * @returns path 在 Y 轴上的中心位置。
 */
const getPathCenterY = (path: string, cachedBounds?: Bounds): number => {
    const bounds = cachedBounds ?? getPathBounds(path);
    return (bounds.top + bounds.bottom) / 2;
};

/**
 * 把文字引擎 path 指令转换为 SVG 节点。
 *
 * @param instruction - 文字引擎 path 指令。
 * @param pathBounds - 可选的本轮原始 path bounds，用于竖排旋转中心计算。
 * @returns 可绘制 path；空轮廓返回 null。
 */
const createPathNode = (
    instruction: Extract<GlyphRenderInstruction, { kind: 'path' }>,
    pathBounds?: Bounds,
): SVGPathElement | null => {
    if (!instruction.d.trim()) return null;
    const rotate =
        instruction.orientation === 'rotated90Cw'
            ? ` rotate(90) translate(0 ${getPathCenterY(instruction.d, pathBounds)})`
            : '';
    const skew = instruction.fakeItalic ? ` skewX(${ITALIC_SKEW_DEG})` : '';
    return createSVGElement('path', {
        d: instruction.d,
        fill: rgbaUint32ToCss(instruction.fillRgba),
        'fill-rule': instruction.fillRule === 'evenOdd' ? 'evenodd' : 'nonzero',
        transform: `translate(${instruction.x} ${instruction.y})${rotate} scale(1 -1)${skew}`,
    });
};

/**
 * 把浏览器委托指令转换为 SVG text 节点。
 *
 * @param instruction - delegated 文字指令。
 * @returns 保留字节范围和字体语义的 SVG text。
 */
const createDelegatedNode = (instruction: Extract<GlyphRenderInstruction, { kind: 'delegated' }>): SVGTextElement => {
    const transformed = instruction.fakeItalic || instruction.orientation === 'rotated90Cw';
    const rotate =
        instruction.orientation === 'rotated90Cw' ? ` rotate(90) translate(0 ${instruction.fontSize * 0.32})` : '';
    const skew = instruction.fakeItalic ? ` skewX(${-ITALIC_SKEW_DEG})` : '';
    const text = createSVGElement('text', {
        x: transformed ? 0 : instruction.x,
        y: transformed ? 0 : instruction.y,
        'font-family': resolveDelegatedFontFamily(instruction.fontFamily, instruction.reason),
        'font-size': instruction.fontSize,
        'font-weight': instruction.fontWeight,
        'font-style': instruction.italic && !instruction.fakeItalic ? 'italic' : 'normal',
        fill: rgbaUint32ToCss(instruction.fillRgba),
        'dominant-baseline': 'alphabetic',
        'xml:space': 'preserve',
        'data-cluster-start': instruction.byteStart,
        'data-cluster-end': instruction.byteEnd,
        ...(transformed ? { transform: `translate(${instruction.x} ${instruction.y})${rotate}${skew}` } : {}),
    });
    text.textContent = instruction.text;
    return text;
};

/**
 * 把一条文字引擎指令转换为普通 SVG 节点。
 *
 * @param instruction - 文字引擎绘制指令。
 * @param pathBounds - 可选的本轮 path bounds；非 path 指令会忽略。
 * @returns 支持的可绘制节点；无需绘制时返回 null。
 */
export const createInstructionNode = (instruction: GlyphRenderInstruction, pathBounds?: Bounds): SVGElement | null => {
    if (instruction.kind === 'path') return createPathNode(instruction, pathBounds);
    if (instruction.kind === 'delegated') return createDelegatedNode(instruction);
    if (instruction.kind === 'decorationRect') {
        if (instruction.width <= 0 || instruction.height <= 0) return null;
        return createSVGElement('rect', {
            x: instruction.x,
            y: instruction.y,
            width: instruction.width,
            height: instruction.height,
            fill: rgbaUint32ToCss(instruction.fillRgba),
        });
    }
    if (instruction.kind === 'missing') {
        return createSVGElement('rect', {
            x: instruction.x,
            y: instruction.y - 14,
            width: 10,
            height: 14,
            fill: '#ef4444',
            opacity: 0.45,
        });
    }
    return null;
};

/**
 * 把正文或字符 marker 的绘制事实转换为 SVG 节点。
 *
 * @param glyph - 文字引擎指令或列表 marker path。
 * @param pathBounds - 可选的本轮正文 path bounds；marker 不消费该值。
 * @returns 可绘制 SVG 节点；空轮廓或不支持的指令返回 null。
 */
export const createGlyphNode = (glyph: GlyphNodeSource, pathBounds?: Bounds): SVGElement | null => {
    if (glyph.kind !== 'marker') return createInstructionNode(glyph, pathBounds);
    if (!glyph.d.trim()) return null;
    return createSVGElement('path', {
        d: glyph.d,
        fill: glyph.fill,
        transform: glyph.transform,
    });
};
